import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight, ArrowDownLeft, ArrowUpRight, Search, FileText, ChevronRight, CheckCircle2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// --- Types ---
interface StatementLine {
  accountCode: "PAYSTACK_CLEARING" | "MERCHANT_PAYABLE" | "DELIVERY_PAYABLE" | "PLATFORM_FEE_REVENUE";
  debitKobo: number;
  creditKobo: number;
}

interface StatementItem {
  id: number;
  eventKey: string;
  type: "charge" | "refund" | "payout" | "payout_reversal";
  sourceId: string;
  occurredAt: string;
  lines: StatementLine[];
}

interface StatementListTotals {
  debitKobo: number;
  creditKobo: number;
  merchantPayable: number;
  deliveryPayable: number;
  platformFeeRevenue: number;
  clearing: number;
}

interface StatementList {
  items: StatementItem[];
  totals: StatementListTotals;
  asOf: string;
  nextCursor: string | null;
}

interface StatementDetail extends StatementItem {
  evidence?: {
    providerConfirmed: boolean;
  };
}

export default function FinanceTab() {
  const [items, setItems] = useState<StatementItem[]>([]);
  const [totals, setTotals] = useState<StatementListTotals>({ debitKobo: 0, creditKobo: 0, merchantPayable: 0, deliveryPayable: 0, platformFeeRevenue: 0, clearing: 0 });
  const [asOf, setAsOf] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<StatementDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchStatements = useCallback(async (cursor?: string, append = false) => {
    if (!cursor) setLoading(true);
    else setLoadingMore(true);
    setListError(null);

    try {
      const params = new URLSearchParams();
      if (filterFrom) params.append("from", filterFrom);
      if (filterTo) params.append("to", filterTo);
      if (filterType && filterType !== "all") params.append("type", filterType);
      if (cursor) params.append("cursor", cursor);
      params.append("limit", "50");

      const res = await fetch(`${BASE}/api/ecommerce/my-statement?${params.toString()}`, {
        credentials: "include",
      });
      
      if (!res.ok) throw new Error("Failed to fetch statements");
      
      const data: StatementList = await res.json();
      
      if (append) {
        setItems(prev => [...prev, ...data.items]);
      } else {
        setItems(data.items);
        setTotals(data.totals);
        setAsOf(data.asOf);
      }
      setNextCursor(data.nextCursor);
    } catch (error) {
      console.error(error);
      setListError(error instanceof Error ? error.message : "Failed to load statements");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterFrom, filterTo, filterType]);

  // Initial fetch and on filter change
  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  const loadDetail = async (id: number) => {
    setSelectedId(id);
    setDetail(null);
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const res = await fetch(`${BASE}/api/ecommerce/my-statement/${id}`, {
        credentials: "include"
      });
      if (!res.ok) throw new Error("Failed to load detail");
      const data: StatementDetail = await res.json();
      setDetail(data);
    } catch (error) {
      console.error(error);
      setDetailError(error instanceof Error ? error.message : "Failed to load detail");
    } finally {
      setLoadingDetail(false);
    }
  };

  const getMerchantNet = (lines: StatementLine[]) => {
    const merchantLine = lines.find(l => l.accountCode === "MERCHANT_PAYABLE");
    if (!merchantLine) return 0;
    // Credit to MERCHANT_PAYABLE increases balance, Debit decreases
    return merchantLine.creditKobo - merchantLine.debitKobo;
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "charge": return "Sale / Order Payment";
      case "refund": return "Refund Deduction";
      case "payout": return "Confirmed Settlement";
      case "payout_reversal": return "Payout reversed";
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "charge": return <ArrowDownLeft className="w-4 h-4 text-emerald-600" />;
      case "refund": return <ArrowUpRight className="w-4 h-4 text-slate-500" />;
      case "payout": return <ArrowUpRight className="w-4 h-4 text-blue-600" />;
      case "payout_reversal": return <ArrowDownLeft className="w-4 h-4 text-amber-600" />;
      default: return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };
  
  const getAccountLabel = (code: string) => {
    switch (code) {
      case "PAYSTACK_CLEARING": return "Payment Gateway";
      case "MERCHANT_PAYABLE": return "Your Balance";
      case "DELIVERY_PAYABLE": return "Delivery Pass-through";
      case "PLATFORM_FEE_REVENUE": return "Platform Fee";
      default: return code;
    }
  };

  const isDateFiltered = Boolean(filterFrom || filterTo);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Financial Statements</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Trace every transaction, fee, and payout with perfect accuracy.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-none border-border/50 bg-card">
          <CardContent className="p-5">
            <div className="text-sm font-medium text-muted-foreground mb-1">
              {isDateFiltered ? "Merchant payable change" : "Merchant Payable"}
            </div>
            <div className={`text-2xl font-bold ${totals.merchantPayable >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {totals.merchantPayable >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totals.merchantPayable) / 100)}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {isDateFiltered ? "Increase/decrease during selected period" : "Amount still owed before confirmed payout"}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-none border-border/50 bg-card">
          <CardContent className="p-5">
            <div className="text-sm font-medium text-muted-foreground mb-1">
              {isDateFiltered ? "Delivery payable change" : "Delivery Pass-through"}
            </div>
            <div className={`text-2xl font-bold ${totals.deliveryPayable >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {totals.deliveryPayable >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totals.deliveryPayable) / 100)}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {isDateFiltered ? "Increase/decrease during selected period" : "Pending payout to delivery partners"}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-none border-border/50 bg-card">
          <CardContent className="p-5">
            <div className="text-sm font-medium text-muted-foreground mb-1">Platform Fees</div>
            <div className={`text-2xl font-bold ${totals.platformFeeRevenue >= 0 ? 'text-slate-700' : 'text-slate-700'}`}>
              {totals.platformFeeRevenue >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totals.platformFeeRevenue) / 100)}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">RetailPulse platform fees</p>
          </CardContent>
        </Card>
        <Card className="shadow-none border-border/50 bg-card">
          <CardContent className="p-5">
            <div className="text-sm font-medium text-muted-foreground mb-1">Clearing</div>
            <div className={`text-2xl font-bold ${totals.clearing >= 0 ? 'text-slate-700' : 'text-slate-700'}`}>
              {totals.clearing >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totals.clearing) / 100)}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Reconciliation indicator</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none border-border/50">
        <CardHeader className="p-4 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
                <div className="space-y-1.5 w-full sm:w-36">
                  <label className="text-xs font-medium text-muted-foreground">From</label>
                  <Input 
                    type="date" 
                    value={filterFrom} 
                    onChange={e => setFilterFrom(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="input-filter-from"
                  />
                </div>
                <div className="space-y-1.5 w-full sm:w-36">
                  <label className="text-xs font-medium text-muted-foreground">To</label>
                  <Input 
                    type="date" 
                    value={filterTo} 
                    onChange={e => setFilterTo(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="input-filter-to"
                  />
                </div>
              </div>
              <div className="space-y-1.5 w-full sm:w-40 mt-4 sm:mt-0">
                <label className="text-xs font-medium text-muted-foreground">Transaction Type</label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-9 text-sm" data-testid="select-filter-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="charge">Sales (Charges)</SelectItem>
                    <SelectItem value="refund">Refunds</SelectItem>
                    <SelectItem value="payout">Settlements</SelectItem>
                    <SelectItem value="payout_reversal">Payout Reversals</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {asOf && (
              <div className="text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-md">
                As of {format(new Date(asOf), "MMM d, h:mm a")}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {listError ? (
            <div className="p-12 text-center text-red-600 bg-red-50/50">
              <FileText className="w-10 h-10 text-red-300 mx-auto mb-3" />
              <p className="text-sm font-medium">Failed to load statements</p>
              <p className="text-xs mt-1 opacity-80">{listError}</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[180px]">Date & Time</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Net Amount</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-48 text-center">
                        <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-sm font-medium text-foreground">No financial records found</p>
                        <p className="text-xs text-muted-foreground mt-1">Adjust your date range or filters to see more.</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map(item => {
                      const net = getMerchantNet(item.lines);
                      const isPositive = net > 0;
                      const isZero = net === 0;
                      
                      return (
                        <TableRow 
                          key={item.id} 
                          className="cursor-pointer hover:bg-muted/30 transition-colors group"
                          onClick={() => loadDetail(item.id)}
                          data-testid={`row-statement-${item.id}`}
                        >
                          <TableCell className="text-sm">
                            <div className="font-medium">{format(new Date(item.occurredAt), "MMM d, yyyy")}</div>
                            <div className="text-xs text-muted-foreground">{format(new Date(item.occurredAt), "h:mm a")}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center shrink-0">
                                {getTypeIcon(item.type)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{getTypeLabel(item.type)}</p>
                                {item.type === "payout" && (
                                  <p className="text-[10px] text-muted-foreground mt-0.5 max-w-[200px] truncate">
                                    Provider-confirmed settlement evidence
                                  </p>
                                )}
                                {item.type === "payout_reversal" && (
                                  <p className="text-[10px] text-amber-700/80 mt-0.5 max-w-[200px] truncate">
                                    Provider-reported payout reversal
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {item.sourceId}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-mono font-medium ${isPositive ? 'text-emerald-600' : isZero ? 'text-slate-500' : 'text-slate-700'}`}>
                              {isPositive ? '+' : isZero ? '' : '-'}{formatCurrency(Math.abs(net) / 100)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              
              {nextCursor && (
                <div className="p-4 border-t flex justify-center bg-muted/10">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchStatements(nextCursor, true)}
                    disabled={loadingMore}
                    className="bg-white"
                    data-testid="button-load-more"
                  >
                    {loadingMore && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Load Older Records
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={selectedId !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Double-entry ledger breakdown for this event.
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetail || !detail ? (
            <div className="py-12 flex justify-center">
              {detailError ? (
                <div className="text-center text-red-600">
                  <FileText className="w-10 h-10 text-red-300 mx-auto mb-3" />
                  <p className="text-sm font-medium">Failed to load transaction details</p>
                  <p className="text-xs mt-1 opacity-80">{detailError}</p>
                </div>
              ) : (
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="capitalize px-2 py-0.5 text-xs font-medium bg-slate-50 text-slate-700">
                      {detail.type}
                    </Badge>
                    <span className="text-sm font-mono text-muted-foreground">{detail.sourceId}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Recorded on {format(new Date(detail.occurredAt), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                </div>
                
                {detail.type === "payout" && detail.evidence?.providerConfirmed && (
                  <div className="bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 border border-blue-100">
                    <CheckCircle2 className="w-4 h-4" />
                    Provider-confirmed settlement evidence
                  </div>
                )}
                {detail.type === "payout_reversal" && (
                  <div className="bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 border border-amber-100">
                    <CheckCircle2 className="w-4 h-4" />
                    Provider-reported payout reversal
                  </div>
                )}
              </div>

              <div className="border rounded-md overflow-hidden bg-card shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Account</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">Debit (Out)</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">Credit (In)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="font-medium text-sm">{getAccountLabel(line.accountCode)}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{line.accountCode}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {line.debitKobo > 0 ? formatCurrency(line.debitKobo / 100) : "—"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {line.creditKobo > 0 ? formatCurrency(line.creditKobo / 100) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-slate-50 border-t p-3 text-xs text-muted-foreground flex justify-between items-center">
                  <span>
                    Your Net Effect: <strong className={`font-mono ml-1 ${getMerchantNet(detail.lines) > 0 ? 'text-emerald-600' : getMerchantNet(detail.lines) < 0 ? 'text-slate-700' : 'text-slate-500'}`}>
                      {getMerchantNet(detail.lines) > 0 ? '+' : getMerchantNet(detail.lines) < 0 ? '-' : ''}{formatCurrency(Math.abs(getMerchantNet(detail.lines)) / 100)}
                    </strong>
                  </span>
                  <span>Journal ID: <span className="font-mono">{detail.id}</span></span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
